import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ChevronDown, Globe } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

export const LanguageSelector = ({ compact = false }) => {
  const { lang, setLang, t, languages } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const current = languages.find((l) => l.code === lang) || languages[0];

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        data-testid="language-selector-button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t.nav.selectLanguage}
        className={`inline-flex items-center gap-1.5 rounded-xl border border-white/10 bg-white/[0.04] text-white/85 hover:text-white hover:bg-white/[0.08] hover:border-white/16 transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#39FF14]/70 ${
          compact ? "px-2 py-2" : "px-2.5 sm:px-3 py-2"
        }`}
      >
        <Globe className="w-4 h-4 text-[#39FF14]" />
        <span className="text-base leading-none" aria-hidden="true">{current.flag}</span>
        {!compact && (
          <span className="font-mono text-[11px] uppercase tracking-wider">{current.code}</span>
        )}
        <ChevronDown className={`w-3.5 h-3.5 text-white/55 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.ul
            key="lang-menu"
            role="listbox"
            data-testid="language-selector-menu"
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
            className="absolute right-0 mt-2 w-44 z-50 rounded-2xl border border-white/12 bg-black/85 backdrop-blur-xl shadow-[0_18px_60px_rgba(0,0,0,0.55)] p-1.5"
          >
            {languages.map((l) => {
              const active = l.code === lang;
              return (
                <li key={l.code}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={active}
                    data-testid={`language-option-${l.code}`}
                    onClick={() => {
                      setLang(l.code);
                      setOpen(false);
                    }}
                    className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-xl text-left text-sm transition-colors ${
                      active
                        ? "bg-[#39FF14]/12 text-white"
                        : "text-white/80 hover:text-white hover:bg-white/[0.07]"
                    }`}
                  >
                    <span className="text-base leading-none" aria-hidden="true">{l.flag}</span>
                    <span className="flex-1 font-medium">{l.name}</span>
                    <span className="font-mono text-[10px] uppercase tracking-wider text-white/45">{l.code}</span>
                    {active && <Check className="w-3.5 h-3.5 text-[#39FF14]" />}
                  </button>
                </li>
              );
            })}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
};
